// src/pages/ProductDetail.jsx
import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { getProductById } from '../services/productApi';
import { acquireFreeProduct } from '../services/orderApi';
import { AuthContext } from '../context/AuthContext';
import { useAddToCart } from '../hooks/useAddToCart';
import AddToCartButton from '../components/AddToCartButton';
import '../styles/ProductDetail.css';
import { API_BASES, SITE_URL } from '../config';
import { IMG_FALLBACK, onImgError } from '../utils/imageFallback';
import { Helmet } from 'react-helmet-async';

const API = API_BASES.gateway;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, role, accessToken, refreshCartCount } = useContext(AuthContext);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Estado de bloqueo por orden en revisión (declared before useAddToCart so it can be passed in)
  const [lastRelevantOrder, setLastRelevantOrder] = useState(null);
  const [isCartLocked, setIsCartLocked] = useState(false);

  // ── "Add to cart" logic lives in a dedicated hook ──────────────────────
  const { isAlreadyInCart, isAdding, addedToCart, handleAddToCart } = useAddToCart(id, {
    isAuthenticated,
    role,
    isCartLocked,
    refreshCartCount,
  });

  // imagen seleccionada (índice en fotografiaUrl)
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Zoom state and ref
  const imgWrapperRef = useRef(null);
  const [zoomStyle, setZoomStyle] = useState({ transform: 'scale(1)', transformOrigin: 'center center' });

  // cambiar imagen
  const selectImage = (i) => {
    setSelectedIndex(i);
    setZoomStyle({ transform: 'scale(1)', transformOrigin: 'center center' });
  };
  const nextImage = () => {
    const n = (product?.fotografiaUrl?.length || 0);
    if (n > 1) selectImage((selectedIndex + 1) % n);
  };
  const prevImage = () => {
    const n = (product?.fotografiaUrl?.length || 0);
    if (n > 1) selectImage((selectedIndex - 1 + n) % n);
  };

  // Cargar producto
  useEffect(() => {
    setLoading(true);
    getProductById(id)
      .then(res => {
        if (res?.data) {
          setProduct(res.data);
          setSelectedIndex(0);
        } else {
          setError('Los datos del producto no están disponibles.');
        }
      })
      .catch((err) => {
        console.error('Error al obtener el producto:', err);
        setError('No se pudo cargar el producto. Es posible que no exista o que haya un problema con la conexión.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Rehidratar estado de "última orden" para saber si debemos bloquear el carrito
  const rehydrateFromLatestOrder = useCallback(async () => {
    try {
      if (!isAuthenticated || role !== 'ROL_CLIENTE') {
        setIsCartLocked(false);
        setLastRelevantOrder(null);
        return;
      }
      const resp = await axios.get(`${API}/api/orders/my-orders/latest`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
      });

      if (resp.status === 404 || !resp.data) {
        // No hay última orden → liberar bloqueo
        setIsCartLocked(false);
        setLastRelevantOrder(null);
        return;
      }

      const dto = resp.data;
      if (dto.paymentStatus === 'UPLOADED_RECEIPT') {
        setIsCartLocked(true);
        setLastRelevantOrder(dto);
      } else if (dto.paymentStatus === 'PAID' || dto.paymentStatus === 'PAID_PAYPAL' || dto.downloadUrl) {
        setIsCartLocked(false);
        setLastRelevantOrder(dto);
      } else {
        setIsCartLocked(false);
        setLastRelevantOrder(null);
      }
    } catch (e) {
      console.debug('rehydrateFromLatestOrder skipped:', e?.message);
    }
  }, [isAuthenticated, role, accessToken]);

  useEffect(() => { rehydrateFromLatestOrder(); }, [rehydrateFromLatestOrder]);

  // Verificar y sanear pendingApprovalOrderId de localStorage (si existe)
  useEffect(() => {
    const pending = Number(localStorage.getItem('pendingApprovalOrderId') || 0);
    if (!pending || !isAuthenticated || role !== 'ROL_CLIENTE') return;

    (async () => {
      try {
        const resp = await axios.get(`${API}/api/orders/${pending}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
        });

        if (resp.status === 404 || !resp.data) {
          // La orden ya no existe → limpiar y liberar
          localStorage.removeItem('pendingApprovalOrderId');
          setIsCartLocked(false);
          setLastRelevantOrder(null);
          return;
        }

        const dto = resp.data;
        if (dto.paymentStatus === 'UPLOADED_RECEIPT') {
          setIsCartLocked(true);
          setLastRelevantOrder(dto);
        } else if (dto.paymentStatus === 'PAYMENT_REJECTED') {
          // Rechazada → liberar
          localStorage.removeItem('pendingApprovalOrderId');
          setIsCartLocked(false);
          setLastRelevantOrder(null);
        } else if (dto.paymentStatus === 'PAID' || dto.paymentStatus === 'PAID_PAYPAL' || dto.downloadUrl) {
          // Aprobada → liberar (aunque podría mostrar botón de descarga en carrito)
          localStorage.removeItem('pendingApprovalOrderId');
          setIsCartLocked(false);
          setLastRelevantOrder(dto);
        } else {
          // Otro estado → no bloquear
          setIsCartLocked(false);
          setLastRelevantOrder(null);
        }
      } catch {
        // Ignorar errores transitorios de red
      }
    })();
  }, [isAuthenticated, role, accessToken]);

  // Descarga inmediata para productos Free (no se bloquea)
  const handleAcquireFree = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (role !== 'ROL_CLIENTE') {
      alert(`Tu rol actual (${role}) no permite descargar productos.`);
      return;
    }
    try {
      setDownloading(true);
      const orderDto = await acquireFreeProduct(id); // crea orden FREE
      const url = orderDto?.downloadUrl;
      const orderId = orderDto?.id;
      if (url) {
        window.location.assign(url); // inicia descarga
        return;
      }
      if (orderId) {
        navigate(`/orders/${orderId}`);
        return;
      }
      alert('La orden gratuita se creó, pero no se recibió un enlace de descarga ni un ID de orden.');
    } catch (err) {
      console.error('Error adquiriendo producto Free:', err);
      const msg = err?.response?.data?.message || 'No se pudo procesar la descarga gratuita.';
      alert(msg);
    } finally {
      setDownloading(false);
    }
  };

  // Helper to get file extension for badges
  const getExt = fn => {
    const parts = fn.split('.');
    return parts.length > 1 ? `.${parts.pop().toUpperCase()}` : '';
  };

  // Zoom logic
  const onMouseMove = e => {
    if (!imgWrapperRef.current) return;
    const { left, top, width, height } = imgWrapperRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({ transform: 'scale(1.5)', transformOrigin: `${x}% ${y}%` });
  };
  const onMouseLeave = () => setZoomStyle({ transform: 'scale(1)', transformOrigin: 'center center' });

  // Render loading, error, or product details
  if (loading) return (
    <Container className="text-center my-5">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Cargando...</span>
      </Spinner>
    </Container>
  );
  if (error) return <Container className="my-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!product) return <Container className="my-5"><Alert variant="info">Producto no encontrado.</Alert></Container>;

  // --- Precio / Free ---
  const priceNum = Number(product?.precioIndividual ?? 0);
  const isFree = Boolean(product?.esGratis) || priceNum === 0;

  // 1. Prepare SEO variables
  const seoTitle = `${product.nombre} | AECBlock`;
  const seoDescription = product.descripcionProd 
    ? product.descripcionProd.substring(0, 160) 
    : `Descarga ${product.nombre} en AECBlock. Herramientas técnicas para arquitectura e ingeniería.`;
  // Inside your ProductDetail component...
  const productUrl = `${SITE_URL}/producto/${id}`;
  const mainImage = product.fotografiaUrl?.[0] || `${SITE_URL}/default-og-image.png`;

  return (
    <>
      <Helmet>
        <title>{`${product.nombre} | AECBlock`}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="AECBlock" />
        <meta property="og:title" content={`${product.nombre} | AECBlock`} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={productUrl} />
        <meta property="og:image" content={mainImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.nombre} | AECBlock`} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={mainImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": product.nombre,
            "image": mainImage,
            "description": product.descripcionProd?.substring(0, 160),
            "brand": {
              "@type": "Brand",
              "name": "AECBlock"
            },
            "offers": {
              "@type": "Offer",
              "url": productUrl,
              "priceCurrency": "USD",
              "price": priceNum,
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <Container className="my-5">
          <Card className="shadow-sm overflow-hidden">
            <Card.Body className="p-4 p-md-5">
              <Row className="g-4">
                {/* Imagen con zoom */}
                <Col xs={12} md={6} lg={5} className="d-flex flex-column align-items-center">
                  <div
                    ref={imgWrapperRef}
                    onMouseMove={onMouseMove}
                    onMouseLeave={onMouseLeave}
                    className="product-image-wrapper rounded position-relative"
                  >
                    {/* Botón anterior */}
                    <button
                      type="button"
                      className="img-nav-btn left"
                      onClick={prevImage}
                      disabled={!product?.fotografiaUrl || product.fotografiaUrl.length <= 1}
                      aria-label="Imagen anterior"
                    >
                      ‹
                    </button>

                    <img
                      src={(product?.fotografiaUrl && product.fotografiaUrl[selectedIndex]) || IMG_FALLBACK}
                      alt={product?.nombre || 'Product Image'}
                      className="product-detail-img"
                      style={zoomStyle}
                      onError={onImgError}
                    />

                    {/* Botón siguiente */}
                    <button
                      type="button"
                      className="img-nav-btn right"
                      onClick={nextImage}
                      disabled={!product?.fotografiaUrl || product.fotografiaUrl.length <= 1}
                      aria-label="Imagen siguiente"
                    >
                      ›
                    </button>
                  </div>

                  {/* Miniaturas */}
                  {Array.isArray(product?.fotografiaUrl) && product.fotografiaUrl.length > 0 && (
                    <div className="thumbs-strip mt-3">
                      {product.fotografiaUrl.map((url, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`thumb-btn ${i === selectedIndex ? 'active' : ''}`}
                          onClick={() => selectImage(i)}
                          aria-label={`Ver imagen ${i + 1}`}
                          title={`Imagen ${i + 1}`}
                        >
                          <img
                            src={url}
                            alt={`Miniatura ${i + 1}`}
                            onError={onImgError}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </Col>

                {/* Detalle del producto */}
                <Col xs={12} md={6} lg={7}>
                  <h1 className="fw-bold">{product.nombre}</h1>

                  {/* Aviso de bloqueo por orden en revisión */}
                  {isCartLocked && (
                    <Alert variant="warning" className="mt-3">
                      <strong>Pago en revisión.</strong> No podrás agregar productos al carrito hasta que el administrador apruebe o rechace tu comprobante.
                    </Alert>
                  )}

                  <p className="fs-3 fw-bold text-primary mt-3">
                    {isFree ? (
                      <Badge bg="success">Free</Badge>
                    ) : (
                      <>${priceNum.toFixed(2)}</>
                    )}
                  </p>

                  <p className="text-secondary">Creador: {product.uploaderUsername || 'Desconocido'}</p>

                  {/* Categorías */}
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    {(product.categorias || []).map((c, i) => (
                      <Badge key={`cat-${i}`} bg="dark" className="px-3 py-1">{c}</Badge>
                    ))}
                  </div>

                  {/* Especialidades */}
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {(product.especialidades || []).map((s, i) => (
                      <Badge key={`sp-${i}`} bg="secondary" className="px-3 py-1">{s}</Badge>
                    ))}
                  </div>

                  <p className="text-secondary mt-2">
                    País: <strong className="text-dark">{product.pais || 'Desconocido'}</strong>
                  </p>

                  <div className="mt-4 text-secondary product-description">
                    {product.descripcionProd || 'No hay descripción disponible para este producto.'}
                  </div>

                  <div className="mt-5">
                    <h3 className="fw-bold mb-3">Formatos de Archivos:</h3>
                    <Row className="g-2">
                      {(product.formatos || []).length > 0 ? (
                        product.formatos.map((ext, i) => (
                          <Col key={i} xs="auto">
                            <Badge bg="info" className="text-dark px-3 py-1 file-badge">
                              .{ext}
                            </Badge>
                          </Col>
                        ))
                      ) : (
                        <Col xs="auto" className="text-muted">N/A</Col>
                      )}
                    </Row>
                  </div>

              {/* Acciones: Free => Descargar; Pago => Añadir al carrito (bloqueable) */}
              <div className="mt-4">
                {isFree ? (
                  isAuthenticated ? (
                    role === 'ROL_CLIENTE' ? (
                      <Button
                        variant="success"
                        size="lg"
                        className="fw-bold w-100"
                        onClick={handleAcquireFree}
                        disabled={downloading}
                      >
                        {downloading ? 'Preparando descarga…' : 'Descargar Gratis'}
                      </Button>
                    ) : (
                      <Alert variant="info" className="text-center">
                        Este producto es gratuito, pero tu rol actual ({role}) no permite descargarlo.
                      </Alert>
                    )
                  ) : (
                    <Alert variant="warning" className="text-center">
                      Necesitas <a href="/login" className="alert-link">Iniciar Sesión</a> para descargar productos gratuitos.
                    </Alert>
                  )
                ) : (
                  isAuthenticated ? (
                    role === 'ROL_CLIENTE' ? (
                      <AddToCartButton
                        onAdd={() => handleAddToCart(product)}
                        isAdding={isAdding}
                        isAlreadyInCart={isAlreadyInCart}
                        isLocked={isCartLocked}
                        addedToCart={addedToCart}
                      />
                    ) : (
                      <Alert variant="info" className="text-center">
                        Este producto no se puede añadir al carrito con su rol actual ({role}).
                      </Alert>
                    )
                  ) : (
                    <Alert variant="warning" className="text-center">
                      Necesitas <a href="/login" className="alert-link">Iniciar Sesión</a> para añadir productos al carrito de compras.
                    </Alert>
                  )
                )}

                    {/* Botón para volver al catálogo */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-3"
                      onClick={() => navigate('/catalog')}
                    >
                      Volver al Catálogo
                    </Button>

                    {/* Acceso rápido al carrito si hay bloqueo */}
                    {isCartLocked && (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2 p-0"
                        onClick={() => navigate('/cart')}
                      >
                        Ir al carrito para ver el estado de tu pago
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
      </Container>
    </>
  );
}
